import { Component, computed, inject, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { faExclamationCircle, faClose, faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';

import { BlockModel, QuestionType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionPositionData } from '../../../models/positioning/question-position-data.interface';
import { Warning } from '../../../models/positioning/warning.model';
import { QuestionTypeConfig } from '../../../models/question-type-config.constants';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { SurveyPositioningService } from '../../../services/survey-positioning.service';

@Component({
  selector: 'empathy-jump-to-position',
  templateUrl: 'jump-to-position.component.html',
  styleUrls: ['./jump-to-position.component.scss', './_jump-to-position-theme.component.scss']
})
export class JumpToPositionComponent implements OnInit {
  warnings$ = signal<Warning[]>([]);
  selectedPosition$ = signal<number>(null);
  moveIntoBlock$ = signal<boolean>(null);
  blockSelected$ = signal<BlockModel>(null);
  isBlockBeginning$ = signal<boolean>(null);
  isBlockEnd$ = signal<boolean>(null);

  anyBlocker$ = computed(() => this.warnings$().some((w: Warning) => w.blockerInd));
  selectedQuestion$ = computed(() => this.positionMoved(this.selectedPosition$()));

  nextQuestionIndex: number;
  questionToMove: Question;
  questionToMoveIsFirstOnBlock: boolean;
  questionToMoveIsLastOnBlock: boolean;
  questionToMoveIndex: number;
  questionsWithBlocks: QuestionPositionData[] = [];

  faCircleExclamation = faExclamationCircle;
  faClose = faClose;
  faExclamationTriangle = faExclamationTriangle;
  questionType = QuestionType;
  questionTypeConfig = QuestionTypeConfig;

  surveyEditorService = inject(SurveyEditorService);

  private surveyPositioningService = inject(SurveyPositioningService);

  constructor(
    private dialogRef: MatDialogRef<JumpToPositionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { questionToMoveId: string }
  ) {
    this.dialogRef.updateSize('812px', '664px');
  }

  isLastElementInBlock(question: Question): boolean {
    return !!this.surveyEditorService
      .surveyEditor$()
      .blocks.all$()
      .find((b) => b.groupedQuestionIds[b.groupedQuestionIds.length - 1] === question.id);
  }

  ngOnInit(): void {
    this.questionsWithBlocks = this.getQuestionsWithBlocksData();

    this.questionToMove = this.surveyEditorService.surveyEditor$().questions.find((q: Question) => q.id === this.data.questionToMoveId);
    this.questionToMoveIndex = this.surveyEditorService.surveyEditor$().questions.findIndex((q: Question) => q.id === this.data.questionToMoveId);
    this.nextQuestionIndex = this.questionToMoveIndex + 1;

    const questionToMoveBlock = this.surveyEditorService.surveyEditor$().blocks.byGroupedQId(this.questionToMove.id);

    this.questionToMoveIsFirstOnBlock = questionToMoveBlock?.groupedQuestionIds[0] === this.questionToMove.id;
    this.questionToMoveIsLastOnBlock = questionToMoveBlock?.groupedQuestionIds[questionToMoveBlock?.groupedQuestionIds.length - 1] === this.questionToMove.id;
  }

  onPickedPosition(
    index: number,
    moveIntoBlock: boolean = false,
    blockSelected: BlockModel = null,
    isBlockBeginning: boolean = false,
    isBlockEnd: boolean = false
  ): void {
    this.warnings$.set(this.surveyPositioningService.checkWarnings(index, this.questionToMove.id));
    if (this.warnings$().length > 0) {
      const container = document.getElementById('jumpToPositionContainer');
      container.scrollTop = 0;
    }

    this.selectedPosition$.set(index);
    this.moveIntoBlock$.set(moveIntoBlock);

    if (moveIntoBlock) {
      this.blockSelected$.set(blockSelected);
    }

    this.isBlockBeginning$.set(isBlockBeginning);
    this.isBlockEnd$.set(isBlockEnd);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onMove(): void {
    const moved = this.surveyPositioningService.forceMove(
      this.questionToMove.id,
      this.questionToMoveIndex,
      this.selectedQuestion$()?.id,
      this.selectedPosition$() === this.surveyEditorService.surveyEditor$().questions.length,
      this.moveIntoBlock$(),
      this.blockSelected$(),
      this.isBlockBeginning$(),
      this.isBlockEnd$()
    );
    if (moved !== false) {
      this.dialogRef.close();
    }
  }

  private positionMoved(startIndex: number): Question {
    return this.surveyEditorService.surveyEditor$().questions[startIndex];
  }

  private getQuestionsWithBlocksData(): QuestionPositionData[] {
    return this.surveyEditorService.surveyEditor$().questions.map((question) => {
      const surveyBlocks = this.surveyEditorService.surveyEditor$().blocks.all$();
      const block = this.surveyEditorService.surveyEditor$().blocks.byGroupedQId(question.id);

      const multiBlock = block ? surveyBlocks.find((b) => b.groupedBlockIds.includes(block.id)) : null;

      return {
        block,
        question,
        isFirstElementInBlock: block?.groupedQuestionIds[0] === question.id || false,
        isLastElementInBlock: block?.groupedQuestionIds[block.groupedQuestionIds.length - 1] === question.id || false,
        isFirstBlockInsideMultiBlock: multiBlock?.groupedBlockIds[0] === block?.id || false,
        isInsideMultiBlock: block ? !!surveyBlocks.find((b) => b.groupedBlockIds.includes(block.id)) : false
      };
    });
  }
}
